import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {DataGrid} from 'react-data-grid';
import 'react-data-grid/lib/styles.css';
import styled from "styled-components";
import {Button, ButtonGroup, InputGroup, Icon, Menu, MenuItem, Popover, Tag} from '@blueprintjs/core';


const _DataGridContainer = styled.div`
    overflow: hidden;

    [role="columnheader"] {
        background-color: var(--rdg-header-background-color, #f9f9f9);
    }

    .filter-cell {
        padding: 0 8px;
        display: flex;
        align-items: center;
        gap: 6px;
        flex-wrap: wrap;

        .filter-cell-header {
            display: flex;
            align-items: center;
            gap: 6px;
            white-space: nowrap;
        }

        .filter-icon-target {
            display: flex;
            align-items: center;
            cursor: pointer;
        }

        .default-filter {
            flex: 1 1 60px;
            min-width: 60px;

            input {
                height: 22px;
                font-size: 12px;
            }
        }
    }


`

const _FilterPopoverContent = styled.div`
    padding: 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 280px;

    .filter-combinator {
        display: flex;
        justify-content: center;
    }

    .filter-condition {
        display: flex;
        align-items: center;
        gap: 4px;
        flex-wrap: nowrap;

        [class*="bp"][class*="-input-group"] {
            flex: 1;
            min-width: 0;

            input {
                height: 24px;
                font-size: 12px;
            }
        }
    }

    .filter-mode-tag {
        cursor: pointer;
        user-select: none;
        flex-shrink: 0;

        &:focus {
            outline: none;
        }

        [class*="bp"][class*="-icon"] {
            margin-right: 4px;
        }
    }

    .filter-add-row {
        display: flex;
        justify-content: space-between;
    }
`


function defaultFilterFn(row, columnKey, term) {
    const parts = columnKey.split('.');

    let value = parts.reduce((acc, part) => acc && acc[part], row);
    if (value && String(value).toLowerCase().includes(term)) return true;

    const parent = parts.length > 1
        ? parts.slice(0, -1).reduce((acc, part) => acc && acc[part], row)
        : row;
    if (parent) {
        if (parent._id && String(parent._id).toLowerCase().includes(term)) return true;
        if (parent.id && String(parent.id).toLowerCase().includes(term)) return true;
    }

    return false;
}

const FILTER_MODES = [
    {key: 'include', label: 'Includes', icon: 'filter-keep', needsText: true},
    {key: 'exclude', label: 'Excludes', icon: 'filter-remove', needsText: true},
    {key: 'empty', label: 'Empty', icon: 'cross-circle', needsText: false},
    {key: 'not-empty', label: 'Not Empty', icon: 'tick-circle', needsText: false},
];

function resolveValue(row, columnKey) {
    const parts = columnKey.split('.');
    return parts.reduce((acc, part) => acc && acc[part], row);
}

function testCondition(row, columnKey, condition, columnFilterFns) {
    const {filterTerm, mode = 'include'} = condition;

    if (mode === 'empty') {
        const val = resolveValue(row, columnKey);
        return val == null || val === '';
    }
    if (mode === 'not-empty') {
        const val = resolveValue(row, columnKey);
        return val != null && val !== '';
    }

    if (!filterTerm) return true;
    const term = filterTerm.toLowerCase();
    const fn = columnFilterFns?.[columnKey] || defaultFilterFn;
    const match = fn(row, columnKey, term);
    return mode === 'exclude' ? !match : match;
}

function filterRows(rows, filters, columnFilterFns) {
    return rows.filter((row) => {
        return Object.keys(filters).every((columnKey) => {
            const colFilter = filters[columnKey];
            const conditions = colFilter.conditions || [];
            const combinator = colFilter.combinator || 'and';

            const active = conditions.filter(c =>
                !FILTER_MODES.find(m => m.key === c.mode)?.needsText || c.filterTerm
            );
            if (active.length === 0) return true;

            if (combinator === 'and') {
                return active.every(c => testCondition(row, columnKey, c, columnFilterFns));
            } else {
                return active.some(c => testCondition(row, columnKey, c, columnFilterFns));
            }
        })
    })
}

function sortRows(column, direction, rows) {
    const comparer = (a, b) => {
        let a_value = column.split('.').reduce(
            (acc, part) => acc && acc[part], a
        );
        let b_value = column.split('.').reduce(
            (acc, part) => acc && acc[part], b
        );
        return (a_value > b_value ? 1 : -1) * (direction === 'ASC' ? 1 : -1);
    };
    return direction === 'NONE' ? rows : [...rows].sort(comparer);
}


function FilterConditionRow({columnKey, index, condition, setFilters}) {
    const modeInfo = FILTER_MODES.find(m => m.key === condition.mode) || FILTER_MODES[0];

    const updateCondition = (updates) => {
        setFilters(prev => {
            const colFilter = {...(prev[columnKey] || {conditions: [], combinator: 'and'})};
            const conditions = [...(colFilter.conditions || [])];
            conditions[index] = {...conditions[index], ...updates};
            return {...prev, [columnKey]: {...colFilter, conditions}};
        });
    };

    const removeCondition = () => {
        setFilters(prev => {
            const colFilter = {...(prev[columnKey] || {conditions: [], combinator: 'and'})};
            const conditions = (colFilter.conditions || []).filter((_, i) => i !== index);
            if (conditions.length === 0) {
                const next = {...prev};
                delete next[columnKey];
                return next;
            }
            return {...prev, [columnKey]: {...colFilter, conditions}};
        });
    };

    const modeTag = (
        <Popover
            interactionKind="hover"
            hoverOpenDelay={150}
            hoverCloseDelay={200}
            placement="bottom-start"
            minimal
            content={
                <Menu small>
                    {FILTER_MODES.map(m => (
                        <MenuItem
                            key={m.key}
                            icon={m.icon}
                            text={m.label}
                            active={condition.mode === m.key}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={(e) => {
                                e.stopPropagation();
                                updateCondition({mode: m.key, ...(m.needsText ? {} : {filterTerm: ''})});
                            }}
                        />
                    ))}
                </Menu>
            }
        >
            <Tag
                className="filter-mode-tag"
                minimal={condition.mode === 'include'}
                intent={condition.mode === 'include' ? 'none' : 'warning'}
                tabIndex={-1}
                size="small"
                icon={modeInfo.icon}
                onMouseDown={(e) => e.preventDefault()}
                onClick={(e) => e.stopPropagation()}
            >
                {!modeInfo.needsText ? modeInfo.label : null}
            </Tag>
        </Popover>
    );

    return (
        <div className="filter-condition">
            <Button
                minimal
                small
                icon="small-cross"
                onClick={removeCondition}
            />
            {modeInfo.needsText ? (
                <InputGroup
                    small
                    value={condition.filterTerm || ''}
                    onChange={(e) => updateCondition({filterTerm: e.target.value})}
                    onKeyDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    placeholder={`${modeInfo.label}...`}
                    leftElement={modeTag}
                />
            ) : (
                modeTag
            )}
        </div>
    );
}


function FilterableHeaderCell({column, filters, setFilters}) {
    const colFilter = filters[column.key];
    const conditions = colFilter?.conditions || [];
    const combinator = colFilter?.combinator || 'and';
    const hasActive = conditions.some(c => {
        const m = FILTER_MODES.find(fm => fm.key === c.mode);
        return m && (!m.needsText || c.filterTerm);
    });

    const showDefault = column.showDefaultFilter;
    const defaultTerm = showDefault
        ? (conditions.find(c => c.mode === 'include')?.filterTerm || '')
        : '';

    const setDefaultTerm = (value) => {
        setFilters(prev => {
            const existing = prev[column.key] || {conditions: [], combinator: 'and'};
            const conds = [...(existing.conditions || [])];
            const idx = conds.findIndex(c => c.mode === 'include');
            if (!value) {
                // Remove the include condition
                if (idx !== -1) conds.splice(idx, 1);
                if (conds.length === 0) {
                    const next = {...prev};
                    delete next[column.key];
                    return next;
                }
            } else if (idx !== -1) {
                conds[idx] = {...conds[idx], filterTerm: value};
            } else {
                conds.push({mode: 'include', filterTerm: value});
            }
            return {...prev, [column.key]: {...existing, conditions: conds}};
        });
    };

    const addCondition = () => {
        setFilters(prev => {
            const existing = prev[column.key] || {conditions: [], combinator: 'and'};
            const conditions = [...(existing.conditions || []), {mode: 'include', filterTerm: ''}];
            return {...prev, [column.key]: {...existing, conditions}};
        });
    };

    const toggleCombinator = () => {
        setFilters(prev => {
            const existing = prev[column.key] || {conditions: [], combinator: 'and'};
            return {...prev, [column.key]: {...existing, combinator: existing.combinator === 'and' ? 'or' : 'and'}};
        });
    };

    const clearAll = () => {
        setFilters(prev => {
            const next = {...prev};
            delete next[column.key];
            return next;
        });
    };

    const [isOpen, setIsOpen] = useState(false);
    const hoverTimeout = useRef(null);

    const openPopover = () => {
        clearTimeout(hoverTimeout.current);
        if (conditions.length === 0) addCondition();
        setIsOpen(true);
    };
    const scheduleClose = () => {
        hoverTimeout.current = setTimeout(() => setIsOpen(false), 300);
    };

    return (
        <div className="filter-cell">
            <span className="filter-cell-header">
            <Popover
                isOpen={isOpen}
                onInteraction={(next) => { if (!next) scheduleClose(); }}
                placement="bottom-start"
                content={
                    <_FilterPopoverContent
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                        onMouseEnter={openPopover}
                        onMouseLeave={scheduleClose}
                    >
                        {conditions.length > 1 && (
                            <div className="filter-combinator">
                                <ButtonGroup>
                                    <Button
                                        small
                                        text="And"
                                        active={combinator === 'and'}
                                        intent={combinator === 'and' ? 'primary' : 'none'}
                                        onClick={toggleCombinator}
                                    />
                                    <Button
                                        small
                                        text="Or"
                                        active={combinator === 'or'}
                                        intent={combinator === 'or' ? 'primary' : 'none'}
                                        onClick={toggleCombinator}
                                    />
                                </ButtonGroup>
                            </div>
                        )}
                        {conditions.map((cond, i) => (
                            <FilterConditionRow
                                key={i}
                                columnKey={column.key}
                                index={i}
                                condition={cond}
                                setFilters={setFilters}
                            />
                        ))}
                        <div className="filter-add-row">
                            <Button small minimal icon="plus" text="Add condition" onClick={addCondition} />
                            {conditions.length > 0 && (
                                <Button small minimal icon="trash" intent="danger" text="Clear" onClick={clearAll} />
                            )}
                        </div>
                    </_FilterPopoverContent>
                }
            >
                <span
                    className="filter-icon-target"
                    onClick={(e) => { e.stopPropagation(); setIsOpen(prev => !prev); }}
                    onMouseEnter={openPopover}
                    onMouseLeave={scheduleClose}
                >
                    <Icon
                        icon={hasActive ? 'filter-keep' : 'filter'}
                        size={12}
                        intent={hasActive ? 'primary' : 'none'}
                    />
                </span>
            </Popover>
            <span>{column.name}</span>
            </span>
            {showDefault && (
                <InputGroup
                    className="default-filter"
                    small
                    value={defaultTerm}
                    onChange={(e) => setDefaultTerm(e.target.value)}
                    onKeyDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                    placeholder={`${FILTER_MODES[0].label}...`}
                />
            )}
        </div>
    );
}


function translateColumns(columns, hasFilters, filters, setFilters) {
    return columns.map(col => {
        const v7 = {...col};

        // formatter → renderCell
        if (col.formatter && !col.renderCell) {
            v7.renderCell = col.formatter;
            delete v7.formatter;
        }

        // locked → frozen
        if (col.locked != null) {
            v7.frozen = col.locked;
            delete v7.locked;
        }

        // filterable → renderHeaderCell
        if (col.filterable && !col.renderHeaderCell) {
            v7.renderHeaderCell = (props) => (
                <FilterableHeaderCell
                    column={props.column}
                    filters={filters}
                    setFilters={setFilters}
                />
            );
        }
        delete v7.filterable;

        return v7;
    });
}


const SearchableDataGrid = (
    {
        columns,
        data,
        defaultSort,
        defaultSortDirection,
        rowHeight,
        headerRowHeight,
        rowClass,
        fitHeight,
        columnFilterFns,
        onCount,
    }) => {
    const containerRef = useRef(null);
    const [maxHeight, setMaxHeight] = useState(window.innerHeight);
    const [filters, setFilters] = useState({});
    const [sorting, setSorting] = useState({
        column: defaultSort ?? 'name',
        direction: defaultSortDirection ?? 'ASC',
    });

    useLayoutEffect(() => {
        if (containerRef.current) {
            const top = containerRef.current.getBoundingClientRect().top;
            setMaxHeight(Math.max(window.innerHeight - top - 40, 200));
        }
    }, [data.length]);

    useEffect(() => {
        const onResize = () => {
            if (containerRef.current) {
                const top = containerRef.current.getBoundingClientRect().top;
                setMaxHeight(Math.max(window.innerHeight - top - 40, 200));
            }
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);

    const hasFilters = useMemo(
        () => columns?.some(c => c.filterable),
        [columns]
    );

    const hasDefaultFilter = useMemo(
        () => columns?.some(c => c.showDefaultFilter),
        [columns]
    );

    const v7Columns = useMemo(
        () => translateColumns(columns || [], hasFilters, filters, setFilters),
        [columns, hasFilters, filters]
    );

    const selection = useMemo(() => {
        const filtered = filterRows(data, filters, columnFilterFns);
        return sortRows(sorting.column, sorting.direction, filtered);
    }, [data, filters, sorting, columnFilterFns]);

    useEffect(() => {
        onCount?.(selection.length, data.length);
    }, [selection.length, data.length]);

    const sortColumns = useMemo(() => {
        if (sorting.direction === 'NONE') return [];
        return [{columnKey: sorting.column, direction: sorting.direction}];
    }, [sorting]);

    const rh = rowHeight || 35;
    const hh = headerRowHeight || (hasDefaultFilter ? 55 : 35);
    const contentHeight = selection.length * rh + hh + 4;
    const height = fitHeight ? contentHeight + 17 : Math.min(contentHeight + rh, maxHeight);

    return (
        <_DataGridContainer ref={containerRef}>
            <DataGrid
                className="rdg-light"
                style={{height}}
                columns={v7Columns}
                rows={selection}
                rowKeyGetter={(row) => row.id ?? row._id}
                rowHeight={rh}
                headerRowHeight={hh}
                sortColumns={sortColumns}
                onSortColumnsChange={(newSortColumns) => {
                    if (newSortColumns.length === 0) {
                        // Toggled off — cycle to NONE
                        setSorting(prev => ({...prev, direction: 'NONE'}));
                    } else {
                        const sc = newSortColumns[newSortColumns.length - 1];
                        setSorting({column: sc.columnKey, direction: sc.direction});
                    }
                }}
                rowClass={rowClass}
                defaultColumnOptions={{sortable: true}}
            />
        </_DataGridContainer>
    )
}


export {SearchableDataGrid};
